import { FileUpload } from '@acme/foundation';
import { Button, Label } from 'flowbite-react';
import { useCallback, useMemo, useState } from 'react';

const MAX_FILE_SIZE_MB = 10;

export type UploadDocumentModalProps = {
  onConfirm: (file: File) => void | Promise<void>;
  onCancel: () => void;
  title?: string;
};

export const UploadDocumentModal = ({
  onConfirm,
  onCancel,
}: UploadDocumentModalProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onFileSelect = useCallback((files: File[]) => {
    setError(null);
    setFile(files[0] ?? null);
  }, []);

  const onFileRemove = useCallback(() => {
    setFile(null);
  }, []);

  const uploadingFiles = useMemo(
    () =>
      file
        ? [
            {
              id: 'selected',
              file,
              status: 'completed' as const,
              progress: 100,
            },
          ]
        : [],
    [file]
  );

  const handleSubmit = useCallback(async () => {
    if (!file) {
      setError('Выберите файл');
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      await onConfirm(file);
    } catch {
      setError('Не удалось отправить документ');
    } finally {
      setIsSubmitting(false);
    }
  }, [file, onConfirm]);

  return (
    <div className="space-y-6">
      <div>
        <div className="text-sm text-gray-500 mb-4">
          Загрузите платежное поручение от агента о том, что фиат доставлен.
        </div>
        <Label className="mb-2 block">Прикрепленные файлы</Label>
        <FileUpload
          accept=".pdf,.docx,.png,.jpg,.jpeg"
          maxSize={MAX_FILE_SIZE_MB}
          multiple={false}
          onFileSelect={onFileSelect}
          onFileRemove={onFileRemove}
          uploadingFiles={uploadingFiles}
        />
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex gap-4 border-t border-gray-200 pt-4">
        <Button
          color="primary"
          onClick={handleSubmit}
          disabled={!file || isSubmitting}
        >
          {isSubmitting ? 'Отправка...' : 'Отправить'}
        </Button>
        <Button color="light" onClick={onCancel} disabled={isSubmitting}>
          Отмена
        </Button>
      </div>
    </div>
  );
};
